import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Image,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Copy,
  Check,
  FolderOpen,
  Grid,
  List,
  Search,
  FileImage,
  File,
} from 'lucide-react';

interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  storageKey: string;
  url: string;
  thumbnailUrl?: string;
  altText?: string;
  caption?: string;
  folder?: string;
  usedIn: string[];
  createdAt: string;
  updatedAt: string;
}

interface MediaResponse {
  data: MediaItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function MediaPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [folderFilter, setFolderFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: mediaResponse, isLoading, error } = useQuery({
    queryKey: ['media', folderFilter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (folderFilter) params.set('folder', folderFilter);
      if (typeFilter) params.set('mimeType', typeFilter);
      params.set('limit', '100');
      const response = await api.get<MediaResponse>(`/media?${params.toString()}`);
      if (!response.success) throw new Error(response.error?.message);
      // API returns { data: [...], pagination: {...} } at top level
      // response.data is the items array directly
      return { data: response.data as unknown as MediaItem[], pagination: (response as unknown as { pagination: MediaResponse['pagination'] }).pagination };
    },
  });

  const { data: storageStatus } = useQuery({
    queryKey: ['media-status'],
    queryFn: async () => {
      const response = await api.get<{ configured: boolean }>('/media/status');
      return response.data;
    },
  });

  const { data: folders } = useQuery({
    queryKey: ['media-folders'],
    queryFn: async () => {
      const response = await api.get<string[]>('/media/folders');
      return response.data || [];
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const params: Record<string, string> = {};
      if (folderFilter) params.folder = folderFilter;
      const response = await api.upload<MediaItem>('/media/upload', file, params);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['media-folders'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/media/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setSelectedItem(null);
    },
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      await uploadMutation.mutateAsync(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  const mediaItems = mediaResponse?.data || [];
  const filteredItems = searchQuery
    ? mediaItems.filter(
        (item) =>
          item.originalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.filename.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : mediaItems;

  if (!storageStatus?.configured) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload and manage images and files
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-yellow-800">Storage Not Configured</h3>
            <p className="text-yellow-700 text-sm mt-1">
              File storage (MinIO/S3) is not configured. Please set the S3_ENDPOINT, S3_ACCESS_KEY, and S3_SECRET_KEY environment variables.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload and manage images and files
          </p>
        </div>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload Files
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search files..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <select
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">All Folders</option>
          {folders?.map((folder) => (
            <option key={folder} value={folder}>
              {folder}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">All Types</option>
          <option value="image">Images</option>
          <option value="application/pdf">PDFs</option>
        </select>

        <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={cn(
              'p-1.5 rounded',
              viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn(
              'p-1.5 rounded',
              viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {uploadMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4" />
          {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload failed'}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-4">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <p className="text-red-700">Failed to load media library</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No files uploaded</h3>
          <p className="text-gray-500 mt-1">Upload images and files to get started.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={cn(
                'bg-white rounded-xl shadow-sm border overflow-hidden cursor-pointer transition-all hover:shadow-md',
                selectedItem?.id === item.id ? 'ring-2 ring-primary-500 border-primary-500' : 'border-gray-200'
              )}
              onClick={() => setSelectedItem(item)}
            >
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {isImage(item.mimeType) ? (
                  <img
                    src={item.url}
                    alt={item.altText || item.originalName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <File className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div className="p-2">
                <p className="text-xs font-medium text-gray-900 truncate">{item.originalName}</p>
                <p className="text-xs text-gray-500">{formatFileSize(item.size)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr
                  key={item.id}
                  className={cn(
                    'hover:bg-gray-50 cursor-pointer',
                    selectedItem?.id === item.id ? 'bg-primary-50' : ''
                  )}
                  onClick={() => setSelectedItem(item)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                        {isImage(item.mimeType) ? (
                          <img
                            src={item.url}
                            alt={item.altText || item.originalName}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <File className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <span className="font-medium text-gray-900 truncate max-w-[200px]">
                        {item.originalName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatFileSize(item.size)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.mimeType}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.url, item.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Copy URL"
                      >
                        {copiedId === item.id ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm('Delete this file?')) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Panel */}
      {selectedItem && (
        <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-xl border-l border-gray-200 z-50 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">File Details</h3>
            <button onClick={() => setSelectedItem(null)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Preview */}
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              {isImage(selectedItem.mimeType) ? (
                <img
                  src={selectedItem.url}
                  alt={selectedItem.altText || selectedItem.originalName}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <FileImage className="h-16 w-16 text-gray-400" />
              )}
            </div>

            {/* Details */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Filename</label>
                <p className="text-sm text-gray-900 break-all">{selectedItem.originalName}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Size</label>
                <p className="text-sm text-gray-900">{formatFileSize(selectedItem.size)}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Type</label>
                <p className="text-sm text-gray-900">{selectedItem.mimeType}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Uploaded</label>
                <p className="text-sm text-gray-900">
                  {new Date(selectedItem.createdAt).toLocaleString()}
                </p>
              </div>

              {selectedItem.folder && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Folder</label>
                  <div className="flex items-center gap-1 text-sm text-gray-900">
                    <FolderOpen className="h-4 w-4 text-gray-400" />
                    {selectedItem.folder}
                  </div>
                </div>
              )}

              {selectedItem.usedIn && selectedItem.usedIn.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Used In</label>
                  <p className="text-sm text-gray-900">{selectedItem.usedIn.length} location(s)</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={selectedItem.url}
                    readOnly
                    className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1 text-gray-600"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedItem.url, selectedItem.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 bg-gray-100 rounded"
                  >
                    {copiedId === selectedItem.id ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <button
                onClick={() => copyToClipboard(selectedItem.url, selectedItem.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {copiedId === selectedItem.id ? (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy URL
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  if (confirm('Delete this file? This cannot be undone.')) {
                    deleteMutation.mutate(selectedItem.id);
                  }
                }}
                disabled={deleteMutation.isPending || (selectedItem.usedIn?.length || 0) > 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {(selectedItem.usedIn?.length || 0) > 0 ? 'In Use - Cannot Delete' : 'Delete File'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
